package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.Navigator
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

open class ServiceContentViewConfiguration {
    var type: EntityViewType = EntityViewType.NORMAL
    var title: String? = null
    var subTitle: String? = null

    /**
     * When true, and no [title] is set yet, render an animated skeleton placeholder in
     * place of the title. This lets a loading view show its heading as a skeleton that
     * occupies the exact same position as the real title, so content doesn't shift when
     * it loads in. Ignored once [title] is non-null.
     */
    var titleSkeleton: Boolean = false

    /**
     * When true, and no [subTitle] is set yet, render an animated skeleton placeholder
     * in place of the subtitle. See [titleSkeleton].
     */
    var subTitleSkeleton: Boolean = false

    /**
     * When true, the entity view container will use height: 100% to exactly fill
     * its parent container, preventing page-level scrolling and enabling fixed-height
     * layouts like split-panel views with independently scrollable regions.
     * When false (default), the entity view grows with its content and scrolls
     * within the overflow container normally.
     */
    var fixedHeight: Boolean = false
    var settings: (@Composable DialogOverlayAttributes.() -> Unit)? = null
    internal var content: (@Composable () -> Unit)? = null
    internal var sidebar: (@Composable () -> Unit)? = null
    internal var help: (@Composable () -> Unit)? = null
    internal var breadcrumbConfig: (BreadcrumbConfiguration.() -> Unit)? = null
    internal var breadcrumbsSet = false
    internal val actions = mutableListOf<ButtonConfiguration>()
    fun breadcrumbs(config: BreadcrumbConfiguration.() -> Unit) {
        if (serviceViewContext != null) {
            serviceViewContext?.breadCrumbs(config)
        } else {
            breadcrumbConfig = config
        }
        breadcrumbsSet = true
    }

    var serviceViewContext: ServiceViewContext? = null

    /**
     * Returns the navigator from the service view context, if available.
     * Used when rendering breadcrumbs directly in ServiceContentView.
     */
    internal val navigator: Navigator?
        get() = serviceViewContext?.navigator

    internal var actionArea: (@Composable () -> Unit)? = null

    fun content(block: @Composable () -> Unit) {
        content = block
    }

    fun action(block: ButtonConfiguration.() -> Unit) {
        actions += ButtonConfiguration().apply(block)
    }

    fun action(title: String, primary: Boolean = false, impl: suspend () -> Unit) {
        actions += ButtonConfiguration(title, primary, impl)
    }

    fun settings(block: @Composable DialogOverlayAttributes.() -> Unit) {
        settings = block
    }

    fun actionArea(block: @Composable () -> Unit) {
        actionArea = block
    }

}

@Composable
fun ServiceViewContext.ServiceContentView(
    builder: ServiceContentViewConfiguration.() -> Unit = {},
    content: @Composable () -> Unit
) = serviceContentView(builder, content)

@Composable
fun ServiceViewContext.serviceContentView(
    builder: ServiceContentViewConfiguration.() -> Unit = {},
    content: @Composable () -> Unit
) {
    val config = ServiceContentViewConfiguration().apply {
        this.serviceViewContext = this@serviceContentView
        builder()
        if (!this.breadcrumbsSet) {
            this@serviceContentView.breadCrumbs(null)
        }
    }
    config.content = content
    serviceContentView(config)
}

@Composable
fun serviceContentView(
    config: ServiceContentViewConfiguration
) {
    // Ensure skeleton styles are available when a title/subtitle placeholder is requested.
    if ((config.title == null && config.titleSkeleton) ||
        (config.subTitle == null && config.subTitleSkeleton)
    ) {
        Style(SkeletonStyles)
    }

    // Main content area. When fixedHeight is enabled, the container fills its parent
    // exactly at 100% height with flex column layout, allowing child components to manage
    // their own scrolling (e.g., split-panel views with independently scrollable regions).
    // When disabled (default), the container uses flex to grow with content and scrolls internally.
    Div({
        classes(ServiceViewStyles.entityView)
        style {
            if (config.fixedHeight) {
                height(100.percent)
                overflow("hidden")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
            } else {
                flex(1)
                overflow("auto")
            }
            minHeight(0.px)
        }
    }) {
        primaryContentArea(fixedHeight = config.fixedHeight) {
            if (config.breadcrumbConfig != null) {
                breadcrumbs(config.navigator, config.breadcrumbConfig!!)
            }

            if (config.settings != null) {
                Div({
                    style {
                        position(Position.Absolute)
                        top(5.px)
                        right(5.px)
                    }
                }) {
                    dialogIcon("settings", config.settings!!)
                }
            }

            // Whether a skeleton placeholder should stand in for a not-yet-loaded title
            // or subtitle. Only applies while the corresponding value is still null.
            val showTitleSkeleton = config.title == null && config.titleSkeleton
            val showSubTitleSkeleton = config.subTitle == null && config.subTitleSkeleton

            if (config.title != null || showTitleSkeleton ||
                config.actionArea != null || config.actions.isNotEmpty()
            ) {
                Div({
                    classes(ServiceViewStyles.titleArea)
                    if (config.settings != null) {
                        style { paddingRight(40.px) }
                    }
                }) {
                    Div {
                        if (config.title != null) {
                            H1 {
                                Text(config.title!!)
                            }
                            config.subTitle?.let {
                                Div({ classes(ServiceViewStyles.subHead) }) {
                                    Text(it)
                                }
                            }
                        } else if (showTitleSkeleton || showSubTitleSkeleton) {
                            // Render the placeholder inside the same H1 / subHead wrappers
                            // the real title uses, so it lands in the exact same position.
                            if (showTitleSkeleton) {
                                H1 {
                                    SkeletonBlock(width = 260.px, height = 1.em, radius = 6.px)
                                }
                            }
                            if (showSubTitleSkeleton) {
                                Div({ classes(ServiceViewStyles.subHead) }) {
                                    SkeletonBlock(width = 140.px, height = 1.em, radius = 6.px)
                                }
                            }
                        }
                    }
                    if (config.actionArea != null) {
                        Div {
                            config.actionArea!!()
                        }
                    } else if (config.type == EntityViewType.NORMAL && config.actions.isNotEmpty()) {
                        Div {
                            config.actions.forEach {
                                button(it)
                            }
                        }
                    }
                }
            }

            config.content?.let { content ->
                Div({
                    if (config.fixedHeight) {
                        style {
                            flex(1)
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                        }
                    }
                }) {
                    content()
                }
            }

            if (config.type == EntityViewType.DIALOG && config.actions.isNotEmpty()) {
                Div({
                    style {
                        // The action row owns the gap above it, since content (e.g. a Box) has no
                        // outer margin.
                        marginTop(20.px)
                        textAlign("right")
                    }
                }) {
                    config.actions.forEach {
                        button(it)
                    }
                }
            }
        }
    }
}